import React, { useState } from "react";
import { Pagination } from "antd";
import { flushSync } from "react-dom";

// Generic table with click-order multi-column sorting: clicking a sortable
// column cycles unsorted -> ascending -> descending -> unsorted, independent
// of every other column's state. Priority among active columns is the order
// they were most recently added to the list (see handleHeaderClick).
const SortableTable = ({
  columns,
  rows,
  page,
  setPage,
  count,
  pageSize,
  onSortChange,
  setPageSize,
  tableStyle = {},
}) => {
  // Ordered list of active sorts, e.g. [{ key: "excNo", direction: "asc" }, ...].
  // Array position IS the priority - index 0 is the primary sort key.
  const [activeSorts, setActiveSorts] = useState([]);

  const handleHeaderClick = (column) => {
    const existingIndex = activeSorts.findIndex((s) => s.key === column);
    let next;

    if (existingIndex === -1) {
      // Not sorted yet -> becomes the lowest-priority active sort.
      next = [...activeSorts, { key: column, direction: "asc" }];
    } else if (activeSorts[existingIndex].direction === "asc") {
      // Same priority slot, just flips direction.
      next = activeSorts.map((s, i) => (i === existingIndex ? { ...s, direction: "desc" } : s));
    } else {
      // Was descending -> drop out of the sort entirely.
      next = activeSorts.filter((_, i) => i !== existingIndex);
    }

    setActiveSorts(next);
    onSortChange?.(next);
  };

  const sortStateFor = (column) => {
    const index = activeSorts.findIndex((s) => s.key === column);
    return {
      direction: index === -1 ? null : activeSorts[index].direction,
      priority: index === -1 ? null : index + 1,
    };
  };

  const onPageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const onSizeChange = (current, newPageSize) => {
    flushSync(() => {
      setPage(1);
      setPageSize(newPageSize);
    });
  };

  const hasGroups = columns.some((c) => c.children?.length);
  const flatColumns = columns.flatMap((c) => (c.children?.length ? c.children : [c]));
  const totalCols = columns.reduce((acc, c) => acc + (c.children?.length || 1), 0);
  const childHeaderRow = hasGroups
    ? columns.flatMap((item) =>
        (item.children || []).map((child, ci) => (
          <th key={child.key || ci} style={{ width: child?.width ? child.width + "px" : "auto" }}>
            {child.title}
          </th>
        ))
      )
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div className="table_holder divcontainer">
        <table id="sortable_table" className="table table-bordered table-striped" style={tableStyle}>
          <thead>
            <tr>
              {columns.map((item, index) =>
                item.children?.length ? (
                  <th key={item.key || index} colSpan={item.children.length} style={{ textAlign: "center" }}>
                    {item.title}
                  </th>
                ) : (
                  <th
                    key={item.key || index}
                    rowSpan={hasGroups ? 2 : 1}
                    onClick={() => item?.sortable && handleHeaderClick(item.key)}
                    style={{
                      width: item?.width ? item.width + "px" : "auto",
                      cursor: item?.sortable ? "pointer" : "default",
                      verticalAlign: hasGroups ? "middle" : undefined,
                      userSelect: "none",
                    }}
                  >
                    <span>{item.title}</span>
                    {item?.sortable &&
                      (() => {
                        const { direction, priority } = sortStateFor(item.key);
                        return (
                          <span style={{ marginLeft: "8px", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                            <span style={{ color: direction === "asc" ? "rgb(215 24 42)" : "#bbb" }}>↑</span>
                            <span style={{ color: direction === "desc" ? "rgb(215 24 42)" : "#bbb" }}>↓</span>
                            {activeSorts.length > 1 && priority && (
                              <span
                                title={`Sort priority ${priority}`}
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  color: "#fff",
                                  background: "rgb(215 24 42)",
                                  borderRadius: "50%",
                                  width: "14px",
                                  height: "14px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  lineHeight: 1,
                                }}
                              >
                                {priority}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                  </th>
                )
              )}
            </tr>
            {hasGroups && <tr>{childHeaderRow}</tr>}
          </thead>
          <tbody>
            {!rows?.length ? (
              <tr>
                <td colSpan={totalCols} className="dataTables_empty" style={{ textAlign: "center", padding: "20px 0" }}>
                  No matching records found
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id || index}>
                  {flatColumns.map((item) => (
                    <td key={item.key || item.dataIndex} style={{ minWidth: item?.width, height: "auto" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent:
                            item.align === "left" ? "flex-start" : item.align === "right" ? "flex-end" : "center",
                          width: "100%",
                          height: "100%",
                          textAlign: item.align || "center",
                          paddingLeft: item.align === "left" ? "10px" : "0",
                        }}
                      >
                        {item.render
                          ? item.render(row[item.dataIndex ?? item.key], row, index)
                          : row[item.dataIndex ?? item.key]}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {count > 0 && (count > 5 || page > 1) && (
        <Pagination
          key={`${page}-${pageSize}`}
          showSizeChanger
          pageSizeOptions={["5", "10", "20", "50"]}
          onShowSizeChange={onSizeChange}
          current={page}
          onChange={onPageChange}
          total={count}
          pageSize={pageSize}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          style={{ marginTop: "10px", marginBottom: "10px", marginLeft: "auto", paddingRight: "10px" }}
        />
      )}
    </div>
  );
};

export default SortableTable;
